import type { BtlApplication } from 'api/btlApplicationForm/types';
import type { MessageKeys, TypedFormatMessageFn } from 'types/intl';
import { CASE_SIDEBAR_SECTIONS } from '../config/sections';
import { ITEM_TITLE_KEYS } from '../config';
import { getVisibleItemIdsForSection, getVisibleSidebarItemIds } from '../resolve/visibleSidebarItems';
import type { CaseSidebarFeatureFlags } from '../types';
import { CaseSidebarItemId, CaseSidebarSection } from '../types';
import { mapCaseSidebarItemToUrlSection, resolveCaseSidebarItemId } from './formSectionMapping';

function getCaseSidebarSectionForItemId(
  itemId: CaseSidebarItemId,
  application: BtlApplication,
  featureFlags: CaseSidebarFeatureFlags = {},
): CaseSidebarSection | undefined {
  for (const section of CASE_SIDEBAR_SECTIONS) {
    const items = getVisibleItemIdsForSection(section, application, featureFlags);

    if (items.includes(itemId)) {
      return section;
    }
  }

  return undefined;
}

export function isLastVisibleItemInCaseSidebarSection(
  itemId: CaseSidebarItemId,
  application: BtlApplication,
  featureFlags: CaseSidebarFeatureFlags = {},
): boolean {
  const section = getCaseSidebarSectionForItemId(itemId, application, featureFlags);

  if (!section) {
    return false;
  }

  const sectionItems = getVisibleItemIdsForSection(section, application, featureFlags);

  return sectionItems[sectionItems.length - 1] === itemId;
}

export function getCaseSidebarNavigationItemIds(
  application: BtlApplication,
  featureFlags: CaseSidebarFeatureFlags = {},
): CaseSidebarItemId[] {
  return [CaseSidebarItemId.OVERVIEW, ...getVisibleSidebarItemIds(application, featureFlags)];
}

export function getAdjacentCaseSidebarItemId(
  currentSection: string,
  application: BtlApplication,
  direction: 'next' | 'previous',
  featureFlags: CaseSidebarFeatureFlags = {},
): CaseSidebarItemId | undefined {
  const currentItemId = resolveCaseSidebarItemId(currentSection);

  if (!currentItemId) {
    return undefined;
  }

  const navigationItemIds = getCaseSidebarNavigationItemIds(application, featureFlags);
  const currentIndex = navigationItemIds.indexOf(currentItemId);

  if (currentIndex === -1) {
    return undefined;
  }

  if (direction === 'next' && isLastVisibleItemInCaseSidebarSection(currentItemId, application, featureFlags)) {
    return undefined;
  }

  const adjacentIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

  return navigationItemIds[adjacentIndex];
}

export function getAdjacentCaseSidebarUrlSection(
  currentSection: string,
  application: BtlApplication,
  direction: 'next' | 'previous',
  featureFlags: CaseSidebarFeatureFlags = {},
): string | undefined {
  const adjacentItemId = getAdjacentCaseSidebarItemId(currentSection, application, direction, featureFlags);

  if (!adjacentItemId) {
    return undefined;
  }

  return mapCaseSidebarItemToUrlSection(adjacentItemId);
}

export function getCaseSidebarNavigationLabel(
  formatMessage: TypedFormatMessageFn,
  currentSection: string,
  application: BtlApplication,
  direction: 'next' | 'previous',
  featureFlags: CaseSidebarFeatureFlags = {},
): string | undefined {
  const adjacentItemId = getAdjacentCaseSidebarItemId(currentSection, application, direction, featureFlags);

  if (!adjacentItemId) {
    return undefined;
  }

  const title = formatMessage({ id: ITEM_TITLE_KEYS[adjacentItemId] as MessageKeys });
  const prefix = direction === 'next' ? 'Next' : 'Back';

  return `${prefix}: ${title}`;
}
